import { Box, Typography } from '@mui/material';
import { Addendum } from '@uplift/api';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useCptCodesByIdsQuery, useDiagnosesByIdsQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import React from 'react';

export type AddendumContentProps = {
  addendum: Addendum;
  providerId: string;
};

const CodeDiffRow: React.VFC<{ label: string; wasLabels: string[]; nowLabels: string[] }> = ({ label, wasLabels, nowLabels }) => (
  <Box display="flex" gap="0.5rem" flexWrap="wrap">
    <Typography variant="body2" color="textSecondary">{label}:</Typography>
    <Typography variant="body2" color="textSecondary" sx={{ textDecoration: 'line-through' }}>
      {wasLabels.join(', ') || 'None'}
    </Typography>
    <Typography variant="body2" color="textPrimary" fontWeight={600}>
      {nowLabels.join(', ') || 'None'}
    </Typography>
  </Box>
);

export const AddendumContent: React.VFC<AddendumContentProps> = ({ addendum, providerId }) => {
  const timezone = useTimezone();
  const createdAt = React.useMemo(() => {
    return CalendarDateService.date(addendum.createdAt).tz(timezone);
  }, [ addendum.createdAt, timezone ]);
  const { data: providerDisplayName } = useUserDisplayNameQuery(providerId, { suspense: true });

  const hasCptChange = Boolean(addendum.cptCodeChangeReason);
  const hasDiagnosisChange = Boolean(addendum.diagnosisCodeChangeReason);

  const cptCodeIds = React.useMemo(() => Array.from(new Set([
    addendum.priorPrimaryCptCodeId,
    addendum.newPrimaryCptCodeId,
    ...addendum.priorAddonCptCodeIds,
    ...addendum.newAddonCptCodeIds,
  ].filter((id): id is string => Boolean(id)))), [ addendum ]);
  const diagnosisIds = React.useMemo(() => Array.from(new Set([
    ...addendum.priorDiagnosisIds,
    ...addendum.newDiagnosisIds,
  ])), [ addendum ]);

  const { data: cptCodes } = useCptCodesByIdsQuery(cptCodeIds, { enabled: hasCptChange && !!cptCodeIds.length });
  const { data: diagnoses } = useDiagnosesByIdsQuery(diagnosisIds, { enabled: hasDiagnosisChange && !!diagnosisIds.length });

  const cptLabel = (id: string | null) => {
    const code = cptCodes?.find(c => c.id === id);

    return code ? `${code.code}: ${code.name}` : null;
  };
  const diagnosisLabels = (ids: string[]) => ids
    .map(id => diagnoses?.find(d => d.id === id))
    .filter((d): d is NonNullable<typeof d> => Boolean(d))
    .map(d => `${d.code}: ${d.name}`);

  return (
    <Box mx={3} bgcolor="grey.50">
      <Box px={3} py={3} display="flex" flexDirection="column">
        {hasCptChange && (
          <Box pb={2}>
            <CodeDiffRow
              label="Primary CPT"
              wasLabels={[ cptLabel(addendum.priorPrimaryCptCodeId) ?? 'None' ]}
              nowLabels={[ cptLabel(addendum.newPrimaryCptCodeId) ?? 'None' ]}
            />
            <CodeDiffRow
              label="Add-on CPT"
              wasLabels={addendum.priorAddonCptCodeIds.map(id => cptLabel(id) ?? id)}
              nowLabels={addendum.newAddonCptCodeIds.map(id => cptLabel(id) ?? id)}
            />
            <Typography variant="body2" color="textSecondary" pt={1} whiteSpace="pre-wrap">
              Reason: {addendum.cptCodeChangeReason}
            </Typography>
          </Box>
        )}
        {hasDiagnosisChange && (
          <Box pb={2}>
            <CodeDiffRow
              label="Diagnosis"
              wasLabels={diagnosisLabels(addendum.priorDiagnosisIds)}
              nowLabels={diagnosisLabels(addendum.newDiagnosisIds)}
            />
            <Typography variant="body2" color="textSecondary" pt={1} whiteSpace="pre-wrap">
              Reason: {addendum.diagnosisCodeChangeReason}
            </Typography>
          </Box>
        )}
        <Typography whiteSpace="pre-wrap">{addendum?.content}</Typography>
        <Box pt={4}>
          <Typography variant="body2" color="textPrimary">
            Submitted by {providerDisplayName?.fullLegalName} on {createdAt.format(DateFormats.DEFAULT_DATE)}, {createdAt.format(DateFormats['hh:mm A'])}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
